/** @jsxImportSource @emotion/react */
/**
 * Галерея посадок
 * Поддерживает группировку по культурам и локациям
 */
import React from 'react';
import { css } from '@emotion/react';
import commonStyles from '../../../styles/commonStyles';
import { Collapse, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Planting } from '../../../models/Planting';
import PlantingCard from './PlantingCard';
import type { CropGroup } from '../hooks/usePlantingsGrouping';

const { Text } = Typography;

interface PlantingsGalleryProps {
  plantings: Planting[];
  onView: (planting: Planting) => void;
  onEdit?: (planting: Planting) => void;
  onDelete?: (planting: Planting) => void;
  groupedData?: CropGroup[]; // Данные с группировкой
  showLocationCode?: boolean; // Показывать ли код локации (по умолчанию зависит от режима)
}

const styles = {
  grid: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }),
  cropTitle: css({ fontSize: 16 }),
  locationHeader: css({ marginBottom: 12, paddingLeft: 8, borderLeft: '3px solid #1890ff' }),
  locationTag: css({ marginLeft: 8 }),
};

const PlantingsGallery: React.FC<PlantingsGalleryProps> = ({
  plantings,
  onView,
  onEdit,
  onDelete,
  groupedData,
  showLocationCode: showLocationCodeProp,
}) => {
  const { t } = useTranslation('plantings');
  // Без группировки - простая сетка
  if (!groupedData) {
    return (
      <div css={styles.grid}>
        {plantings.map((planting) => (
          <PlantingCard
            key={planting.id}
            planting={planting}
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
            showLocationCode={showLocationCodeProp ?? true}
          />
        ))}
      </div>
    );
  }

  // С группировкой - Collapse с сетками
  return (
    <Collapse
      items={groupedData.map((cropGroup) => ({
        key: cropGroup.cropKey,
        label: (
          <Space>
            <Text strong css={styles.cropTitle}>
              {cropGroup.cropName}
            </Text>
            {cropGroup.cropLatinName && (
              <Text type="secondary" css={commonStyles.smallText}>
                {cropGroup.cropLatinName}
              </Text>
            )}
            <Tag color="blue">{t('harvestFacts.units', { count: cropGroup.totalQuantity ?? cropGroup.totalCount })}</Tag>
          </Space>
        ),
        children: (
          <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
            {cropGroup.locations.map((location) => (
              <div key={location.locationName}>
                <div css={styles.locationHeader}>
                  <Text strong>{location.locationName}</Text>
                  <Tag css={styles.locationTag}>{t('harvestFacts.units', { count: location.totalQuantity ?? location.count })}</Tag>
                </div>
                <div css={styles.grid}>
                  {location.items.map((planting) => (
                    <PlantingCard
                      key={planting.id}
                      planting={planting}
                      onView={onView}
                      onEdit={onEdit}
                      onDelete={onDelete}
                    showLocationCode={showLocationCodeProp ?? false}
                    />
                  ))}
                </div>
              </div>
            ))}
          </Space>
        ),
      }))}
    />
  );
};

export default PlantingsGallery;
